<template>
  <aside class="right-sidebar">
    <div class="sidebar-content">
      <!-- Current User -->
      <div v-if="currentUser" class="user-card">
        <div class="user-avatar">
          <img
            v-if="currentUser.user_thumb"
            :src="getUserThumb(currentUser.user_thumb)"
            alt="User Avatar"
          />
          <v-icon v-else size="20" color="white">mdi-account</v-icon>
        </div>
        <div class="user-info">
          <span class="user-name">{{ currentUser.user_name }}</span>
          <span class="user-email">{{ truncateEmail(currentUser.user_email) }}</span>
        </div>
      </div>

      <div
        v-if="isCustomer"
        class="sidebar-section account-status-section"
      >
        <h3 class="sidebar-title">{{ t('menus.rightSidebar.user.accountStatus.title') }}</h3>
        <div
          class="status-indicator"
          :class="{ 'status-indicator--pending': accountStatusCode !== 10 }"
        >
          <v-icon size="12" :color="accountStatusColor" class="mr-2">mdi-circle</v-icon>
          <span class="status-text">{{ accountStatusLabel }}</span>
        </div>
     
        <button
          type="button"
          class="sidebar-link-card link-button mt-2"
          @click="showAccountDialog = true"
        >
          <v-icon size="16" class="mr-2">mdi-account-cog</v-icon>
          {{ t('menus.rightSidebar.user.accountStatus.manageAccount') }}
        </button>
      </div>

      <!-- Company Admins -->
      <div
        v-if="showAdminsInSidebar"
        class="sidebar-section"
      >
        <h3 class="sidebar-title">
          <v-icon
            size="16"
            class="mr-1"
            color="#EA5545"
          >
            mdi-account-group
          </v-icon>
          {{ t('menus.rightSidebar.user.adminsTitle') }}
        </h3>
    

        <!-- Loading State -->
        <div v-if="isLoadingAdmins" class="state-block" data-state="loading" data-size="xs">
          <v-progress-circular indeterminate color="#EA5545" size="20"></v-progress-circular>
        </div>

        <!-- Admins List -->
        <div v-else-if="companyAdmins.length > 0" class="admins-list">
          <div
            v-for="admin in companyAdmins"
            :key="admin.user_id"
            class="admin-item"
          >
          <div class="admin-avatar">
            <img
              v-if="admin.user_thumb"
              :src="getUserThumb(admin.user_thumb)"
              alt="Admin Avatar"
            />
              <v-icon v-else size="16" color="white">mdi-account</v-icon>
            </div>
            <div class="admin-info">
              <span class="admin-name">{{ admin.user_admin_name || admin.user_name }}</span>
              <span class="admin-email">{{ admin.com_name || truncateEmail(admin.user_email) }}</span>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else class="state-block" data-state="empty" data-size="xs">
          <p class="state-text" data-size="xs">{{ t('menus.rightSidebar.user.noAdmins') }}</p>
        </div>
      </div>

      
      <v-dialog
        v-model="showAccountDialog"
        max-width="720"
      >
        <v-card class="account-dialog-card" elevation="2">
          <v-card-title class="account-dialog-title">
            <div class="dialog-title-text">
              <v-icon size="20" color="#EA5545" class="mr-2">mdi-account-cog</v-icon>
              {{ t('menus.rightSidebar.user.accountStatus.manageAccount') }}
            </div>
            <v-btn icon variant="text" @click="showAccountDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="account-dialog-content">
            <UserAccountSettings />
          </v-card-text>
        </v-card>
      </v-dialog>
    </div>
  </aside>
</template>

<script setup>
import { ref, onMounted, inject, watch, computed } from 'vue'
import { useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useLanguage } from '@/composables/useLanguage'
import UserAccountSettings from '@/components/objects/user/UserAccountSettings.vue'

const baseCalls = inject('baseCalls')
const userStore = inject('userStore')
// const router = useRouter()
const { t } = useLanguage()

const { userData } = storeToRefs(userStore)
const route = useRoute()

const currentUser = computed(() => userData.value || null)
const isCustomer = computed(() => Number(currentUser.value?.user_admin ?? 0) === 0)
const inlineAdminRoutes = ['home', 'vertrag']
const showAdminsInSidebar = computed(() => {
  if (!isCustomer.value) return true
  const currentRoute = route.name
  return !inlineAdminRoutes.includes(currentRoute)
})
const accountStatusCode = computed(() => Number(currentUser.value?.user_status ?? 0))
const accountStatusLabel = computed(() => {
  if (accountStatusCode.value === 10) {
    return t('menus.rightSidebar.user.accountStatus.active')
  }
  return t('menus.rightSidebar.user.accountStatus.pending')
})
const accountStatusColor = computed(() => (accountStatusCode.value === 10 ? '#4caf50' : '#fb8c00'))

// State
const companyAdmins = ref([])
const isLoadingAdmins = ref(false)
const showAccountDialog = ref(false)

// Functions
async function loadCompanyAdmins() {
  isLoadingAdmins.value = true
  try {
    const response = await baseCalls.userPost({
      action: 'getcompanyadmins'
    })
    
    if (response.data.success && response.data.admins) {
      companyAdmins.value = response.data.admins
    }
  } catch (err) {
    console.error('Error loading company admins:', err)
  } finally {
    isLoadingAdmins.value = false
  }
}

// function openHelpCenter() {
//   router.push({ name: 'help' })
// }

function truncateEmail(email) {
  if (!email) return ''
  if (email.length <= 20) return email
  const [name, domain] = email.split('@')
  if (name.length > 10) {
    return name.substring(0, 10) + '...@' + domain
  }
  return email
}

function getUserThumb(hash) {
  if (!hash) return null
  return `/getfile/${hash}`
}

// Watch for user changes
watch(userData, async (newValue, oldValue) => {
  if (newValue && (!oldValue || newValue.user_thumb !== oldValue.user_thumb)) {
    await loadCompanyAdmins()
  }
})

// Initial load
onMounted(() => {
  if (userData.value) {
    loadCompanyAdmins()
  }
})
</script>

<style scoped src="@/assets/styles/sfc/components/modules/RightSidebar.css"></style>
